import { Auth } from '@aws-amplify/auth';
import head from 'lodash/head';
import includes from 'lodash/includes';

import { IUserData } from 'store/reducers/userReducer/userTypes';
import { engineAPI, setAuthorization } from 'api/axiosClient';
import Users from 'api/classes/Users';
import {
  IAwsUserSignInRequestProps,
  IAwsUserSignUpRequestProps,
  ICheckEmailProps,
  IForgotPasswordSubmitRequestProps,
  IGetCurrentUserRequestProps,
  ILoginUserRequestProps,
} from 'api/requests/userRequestsTypes';
import { collectSearchParams } from 'utils/collectSearchParams';
import decryptBytes from 'utils/decryptBytes';
import history from 'utils/history';
import { mixpanelTrack } from 'utils/mixpanel/mixpanel';
import { EMixpanelDataKeys, EMixpanelEvents } from 'utils/mixpanel/mixpanelTypes';
import sendGTM from 'utils/sendGTM';
import sendNotification, { ENotificationTypes } from 'utils/sendNotification';
import { isDevelopment, isProdEnv, STORAGE_KEYS } from 'utils/constants/constants';
import { GTM_REGISTRATION_USER_CREATED } from 'utils/constants/gtmConstants';
import { EProviderBySource } from 'types/commonEnums';

export const awsUserSignUpRequest = async(payload: IAwsUserSignUpRequestProps) => {
  try {
    const data = await Auth.signUp(payload);

    return { success: true, data };
  } catch (error: any) {
    let errorMessage;

    if (error.response && error.response.data.message) {
      errorMessage = error.response.data.message;
    } else {
      errorMessage = error.message;
    }

    return { success: false, error: errorMessage, code: error.code };
  }
};

export const awsUserSignInRequest = async(payload: IAwsUserSignInRequestProps) => {
  try {
    const { signInUserSession } = await Auth.signIn(payload);

    return {
      success: true,
      data: {
        token: signInUserSession.idToken.jwtToken,
        permissions: signInUserSession.idToken.payload['cognito:groups'],
        isEmailVerified: signInUserSession.idToken.payload.email_verified,
      },
    };
  } catch (error: any) {
    let errorMessage;

    if (error.response && error.response.data.message) {
      errorMessage = error.response.data.message;
    } else {
      errorMessage = error.message;
    }

    return { success: false, error: errorMessage };
  }
};

export const checkEmailRequest = async(email: ICheckEmailProps) => {
  try {
    const { success, data }: any = await Users.checkEmail(email);

    return { success, data: { ...data, isFounded: data?.type !== 'UserNotFound' } };
  } catch (error: any) {
    let errorMessage;

    if (error.response && error.response.data.message) {
      errorMessage = error.response.data.message;
    } else {
      errorMessage = error.message;
    }

    return { success: false, error: errorMessage };
  }
};

export const loginUserRequest = async({ email, password }: ILoginUserRequestProps) => {
  const { success, data, error } = await awsUserSignInRequest({ username: email, password });

  if (success) {
    return { success, data };
  }

  return { success, error };
};

interface IGetUsersListResponse {
  success: boolean,
  data?: any[],
  error?: string | {} | null
}

export const getCurrentUserRequest = async({
  idToken,
  permissions,
  isEmailVerified,
  needRegister,
}: IGetCurrentUserRequestProps) => {
  engineAPI.defaults.headers.common.Authorization = `Bearer ${idToken}`;
  const { success, data, error }: IGetUsersListResponse = await Users.getUsersList({ originalUser: 'true' });

  if (success) {
    const { email, name, id, source, status } = head(data);

    if (status !== 7 && !needRegister && !includes(permissions, 'Admin')) {
      localStorage.setItem(STORAGE_KEYS.loginError, 'true');

      return { error: true };
    }

    let currentUser: Omit<IUserData, 'permissions' | 'isEmailVerified'> | undefined = head(data);
    const userSelection = localStorage.getItem(STORAGE_KEYS.userSelection);
    const query = new URLSearchParams(history.location.search);
    const { ref: referrer } = collectSearchParams(query);

    if (needRegister) {
      const phoneNumber = localStorage.getItem(STORAGE_KEYS.phoneNumber);
      const fromShopify = localStorage.getItem(STORAGE_KEYS.fromShopifySocial);

      const updateUserPayload = {
        referrer,
        phoneNumber,
        language: 1,
        status: 7,
        fromChannel: fromShopify && 3 || undefined,
      };

      localStorage.removeItem(STORAGE_KEYS.phoneNumber);
      const {
        success: updateSuccess,
        data: updateUser,
      }: any = await Users.updateUser({ userId: currentUser?.id }, updateUserPayload);

      await Users.initUser({ cognitoUserGroup: 'Supplier', autodsUserId: id });
      if (updateSuccess) {
        currentUser = updateUser;
        const currentAuthenticatedUser = await Auth.currentAuthenticatedUser();
        const currentSession = await Auth.currentSession();

        const refreshSession = async() =>
          new Promise((resolve) => {
            // @ts-ignore
            currentAuthenticatedUser.refreshSession(currentSession.refreshToken, async(_, session) => {
              const token = await session.getIdToken().getJwtToken();

              resolve(token);
            });
          });
        const token = await refreshSession();

        setAuthorization({ token });
      }

      sendGTM({
        ...GTM_REGISTRATION_USER_CREATED,
        supplier_email: email,
        supplier_full_name: name,
        supplier_phone_number: phoneNumber,
        supplier_userid: id,
        supplier_authentication_method: EProviderBySource[source],
      });
      mixpanelTrack(EMixpanelEvents.UserCreated, {
        [EMixpanelDataKeys.Email]: email,
        [EMixpanelDataKeys.Name]: name,
        [EMixpanelDataKeys.Phone]: phoneNumber,
        [EMixpanelDataKeys.DistinctId]: id,
        [EMixpanelDataKeys.AuthenticationMethod]: EProviderBySource[source],
      });

      history.push({ search: '?modal_name=tell_us' });
    }

    const user = { ...currentUser, name: currentUser?.name.replace(';', ''), permissions, isEmailVerified };

    window.user_name = user.name;
    window.user_email = user.email;
    !isProdEnv && userSelection
    && window.console.info('%cShowing as User:', 'color: #00D6F7', JSON.parse(userSelection));
    !isProdEnv && window.console.info('%cAutoDS User:', 'color: #00D6F7', user);
    !isDevelopment && window.mixpanel?.autods.identify(user.id);

    return { data: userSelection ? JSON.parse(userSelection) : user, error: false };
  }

  return { error };
};

export const awsForgotPasswordRequest = async(email: string) => {
  try {
    await Auth.forgotPassword(email);

    return {
      success: true,
      data: {
        message: 'Check your email for restore password',
      },
    };
  } catch (error: any) {
    let errorMessage;

    if (error.response?.data.message) {
      errorMessage = error.response.data.message;
    } else {
      errorMessage = error.message;
    }

    return { success: false, error: errorMessage, code: error.code };
  }
};

export const emailConfirmationRequest = async(email: string) => {
  const { success, error }: any = await Users.emailConfirmation({ email });

  return { success, data: { message: 'Check your email for confirmation' }, error };
};

export const forgotPasswordRequest = async(email: string) => {
  const { success: awsSuccess, data: awsData, error: awsError, code } = await awsForgotPasswordRequest(email);
  let response;

  if (awsSuccess) {
    response = {
      success: awsSuccess,
      data: awsData,
    };
  } else {
    if (code === 'InvalidParameterException') {
      const { success, data, error } = await emailConfirmationRequest(email);

      if (success) {
        response = { success, data };
      } else {
        response = { success, error };
      }
    } else {
      response = { success: awsSuccess, error: awsError };
    }
  }

  return response;
};

export const forgotPasswordSubmitRequest = async({ code, password, email }: IForgotPasswordSubmitRequestProps) => {
  await Auth.forgotPasswordSubmit(decryptBytes(email), code, password);
  sendNotification(ENotificationTypes.Success, 'Your password has been updated');
};
